/**
 * The affiliate portal's rail: its tabs, in order, with their groups.
 *
 * Kept out of App.tsx so the tour can draw the very same rail without
 * importing the whole application.
 */
import type { IconName } from '@affdice/portal-kit';

import { t, type AffiliateMessageKey } from './messages.ts';

export type Tab =
  | 'dashboard' | 'links' | 'sources' | 'postbacks' | 'terms' | 'spend' | 'statement'
  | 'payments' | 'statements' | 'history' | 'disputes' | 'players'
  | 'subaffiliates' | 'programmes' | 'campaigns' | 'support' | 'account' | 'reports';

/*
 * The groups name runs of tabs that were already adjacent — where traffic
 * comes from, what it earned, who it brought. Nothing was reordered to make
 * them; the comments below say why each tab sits where it does.
 */
export const TABS: { id: Tab; labelKey: AffiliateMessageKey; icon: IconName; groupKey?: AffiliateMessageKey }[] = [
  { id: 'dashboard', labelKey: 'nav.dashboard', icon: 'dashboard' },
  /*
   * Beside the dashboard and outside every group, which is where the operator
   * and brand consoles put it (affdice-admin/screens.ts). It sat inside
   * Traffic here, so the same two screens were the first pair in one console
   * and one-plus-buried-in-a-group in another, and an affiliate told to "check
   * your reports" was reading a group heading to find them. One rail shape,
   * three consoles.
   */
  { id: 'reports', labelKey: 'nav.reports', icon: 'reports' },
  // Second, not last. It is the screen an affiliate opens most: every other
  // one reports on traffic, and this is where traffic comes from.
  { id: 'links', labelKey: 'nav.links', icon: 'links', groupKey: 'nav.group.traffic' },
  /*
   * §7.12d / §10.11a — the campaigns open to this affiliate, and what each
   * pays them.
   *
   * After Links rather than before it, because Links is the screen opened
   * most; but a link is made *from* a campaign, and until this existed the
   * only way to read a campaign's terms was a dialogue you had to know to
   * open, one campaign at a time. An affiliate on a hundred brands could not
   * answer "where am I actually making money" at all.
   */
  { id: 'campaigns', labelKey: 'nav.campaigns', icon: 'links', groupKey: 'nav.group.traffic' },
  // §39.6 — what the labels on those links are for. Without this screen the
  // sub-ID boxes are a form that does nothing.
  { id: 'sources', labelKey: 'nav.sources', icon: 'sources', groupKey: 'nav.group.traffic' },
  /*
   * Every report, in one place, under the name the affiliate went looking for.
   *
   * Each of these existed and each was somewhere else — the funnel on the
   * dashboard, the click log under Links, quarantine under Players, the pivot
   * under Sources. That is a defensible filing for somebody who already knows
   * the console and no use at all to somebody who has been told "check your
   * reports": they open Reports, and until now there was no Reports.
   */
  // §41.7 — where the affiliate tells their own tracker about the traffic
  // they sent. It sat in Account, among settings about the person; it is about
  // traffic, and belongs beside the links and sources it reports on.
  { id: 'postbacks', labelKey: 'nav.postbacks', icon: 'postbacks', groupKey: 'nav.group.traffic' },
  // The answer to "what am I actually paid, and on what conditions". It sits
  // beside Commission because that is where the question gets asked.
  { id: 'terms', labelKey: 'nav.terms', icon: 'terms', groupKey: 'nav.group.earnings' },
  // §26.12 — declaring media spend on a spend-share deal, and the state of
  // every claim. Beside Terms: it is about what is owed on a deal, not what
  // has already been earned (Commission, next).
  { id: 'spend', labelKey: 'nav.spend', icon: 'invoices', groupKey: 'nav.group.earnings' },
  { id: 'statement', labelKey: 'nav.statement', icon: 'commission', groupKey: 'nav.group.earnings' },
  // §30.1 — the account of each period, which is a different question from
  // "when do I get paid". Payments answers the second; this answers the first.
  { id: 'statements', labelKey: 'nav.statements', icon: 'statements', groupKey: 'nav.group.earnings' },
  { id: 'payments', labelKey: 'nav.payments', icon: 'invoices', groupKey: 'nav.group.earnings' },
  // §42.7 — what changed, when, and why. Beside Players rather than buried in
  // Account, because it is about money and not about settings.
  { id: 'history', labelKey: 'nav.history', icon: 'history', groupKey: 'nav.group.earnings' },
  // Ch. 33 — a disagreement needs somewhere to go that is not an inbox.
  { id: 'disputes', labelKey: 'nav.disputes', icon: 'disputes', groupKey: 'nav.group.earnings' },
  { id: 'players', labelKey: 'nav.players', icon: 'players', groupKey: 'nav.group.network' },
  // §8.9 — shown once the programme is known to be on for this operator, and
  // kept even once it is switched off again if this affiliate already has
  // recruits (their overrides keep being paid); see `subAffiliatesEnabled` in
  // `App` below.
  { id: 'subaffiliates', labelKey: 'nav.subaffiliates', icon: 'network', groupKey: 'nav.group.network' },
  /*
   * "Find brands", not "Brands".
   *
   * The dashboard already has a Brand filter that means the brands this
   * affiliate works with, and its empty option reads "All brands". A nav item
   * called Brands beside it meant something else entirely — brands they do not
   * work with and might apply to — and one word doing two jobs in one app is a
   * word doing neither.
   *
   * This tab is about discovery, so it says so, and the filter keeps the plain
   * word for the thing somebody already has.
   */
  { id: 'programmes', labelKey: 'nav.programmes', icon: 'discover', groupKey: 'nav.group.network' },
  // Opened from the identity at the foot of the rail, as in the other two
  // consoles, so it is not drawn in the list — but it stays a tab, so
  // #account still lands on it.
  /*
   * Support sits at the end of the rail rather than beside Disputes, which is
   * the one place it would be misread. A dispute is a claim about money with
   * an outcome; this is a question. Putting them next to each other invites
   * somebody to file "my link 404s" as a dispute, which is the queue whose SLA
   * is measured and whose analytics find systemic defects.
   */
  { id: 'support', labelKey: 'nav.support', icon: 'disputes' },
  { id: 'account', labelKey: 'nav.account', icon: 'account' },
];

/** One rail entry as `NavList` takes it: labelled in the viewer's language. */
export interface RailItem {
  readonly id: Tab;
  readonly label: string;
  readonly icon: IconName;
  readonly group?: string;
}

/**
 * The tabs the rail draws, in order. Account is opened from the identity at
 * the foot, and Sub-affiliates only where the programme is on for this
 * affiliate.
 */
export function railItems(subAffiliatesEnabled: boolean): RailItem[] {
  return TABS.filter((item) => item.id !== 'account')
    .filter((item) => item.id !== 'subaffiliates' || subAffiliatesEnabled)
    .map((item) => ({
      id: item.id,
      label: t(item.labelKey),
      icon: item.icon,
      ...(item.groupKey ? { group: t(item.groupKey) } : {}),
    }));
}
